// Pastilles : statut (menu pour l'équipe), type, canal d'origine, agent.
// Portées de FeedbackStatusPill, FeedbackTypePill, FeedbackSourcePill.
import type { JSX } from 'preact'
import { FEEDBACK_STATUSES, type FeedbackStatus, type FeedbackType } from '@feedbaacks/sdk'
import { useFeedbaacks } from '../context'
import type { MessageKey } from '../i18n'
import { Bot, Bug, Check, ChevronDown, Graduation, HelpCircle, Lightbulb, Mic, Phone, Plug, Refresh, Send, Sparkles, Terminal, type Icon } from '../icons'
import { Menu } from '../primitives'
import { cx } from '../util'

export const STATUS_KEY: Record<FeedbackStatus, MessageKey> = {
  open: 'status_open', under_review: 'status_under_review', planned: 'status_planned', in_progress: 'status_in_progress',
  on_hold: 'status_on_hold', shipped: 'status_shipped', declined: 'status_declined', duplicate: 'status_duplicate',
}

export const TYPE_META: Record<FeedbackType, { key: MessageKey; plural: MessageKey; hint: MessageKey; Icon: Icon }> = {
  feature: { key: 'type_feature', plural: 'type_features', hint: 'type_feature_hint', Icon: Lightbulb },
  bug: { key: 'type_bug', plural: 'type_bugs', hint: 'type_bug_hint', Icon: Bug },
  question: { key: 'type_question', plural: 'type_questions', hint: 'type_question_hint', Icon: HelpCircle },
}

export function StatusPill({ status, canChange, onChange }: {
  status: FeedbackStatus
  canChange?: boolean
  onChange?: (next: FeedbackStatus) => void
}): JSX.Element {
  const { t } = useFeedbaacks()
  const label = t(STATUS_KEY[status])
  if (!canChange || !onChange) {
    return <span class={cx('fb-pill', `fb-status-${status}`)} data-testid="feedbaacks-status">{label}</span>
  }
  return (
    <Menu<FeedbackStatus>
      value={status}
      options={FEEDBACK_STATUSES.map((s) => ({ value: s, label: t(STATUS_KEY[s]) }))}
      onChange={onChange}
      label={t('status_change_aria', { label })}
      testId="feedbaacks-status-menu"
      renderTrigger={(open) => (
        <span class={cx('fb-pill', `fb-status-${status}`)} data-testid="feedbaacks-status">
          {label}
          <ChevronDown size={11} class={cx('fb-chevron', open && 'is-open')} />
        </span>
      )}
      renderOption={(o, active) => (
        <>
          <span class={cx('fb-status-dot', `fb-status-${o.value}`)}>{active && <Check size={10} />}</span>
          <span class={active ? 'fb-strong' : undefined}>{o.label}</span>
        </>
      )}
    />
  )
}

export function TypeLabel({ type }: { type: FeedbackType }): JSX.Element {
  const { t } = useFeedbaacks()
  return <span class={cx('fb-type-label', `fb-type-${type}`)}>{t(TYPE_META[type].key)}</span>
}

const SOURCE_ICON: Record<string, Icon> = {
  mcp: Plug, cli: Terminal, ai: Sparkles, voice: Mic, phone: Phone, caast: Refresh, academy: Graduation,
}

/** Canal d'origine : rien pour la saisie dans l'interface (web, widget). */
export function SourcePill({ source }: { source: string }): JSX.Element | null {
  const { t, options } = useFeedbaacks()
  if (!source || source === 'web' || source === 'widget') return null
  const label = options.sourceLabels?.[source] ?? t(`source_${source}` as MessageKey, undefined, source)
  const SourceIcon = SOURCE_ICON[source]
  return (
    <span class="fb-pill fb-source" title={label}>
      {SourceIcon && <SourceIcon size={11} />}
      {label}
    </span>
  )
}

/** Qui traite le retour : la ferme d'agents ou la tour de contrôle. */
export function AgentChip({ by = 'agent' }: { by?: 'agent' | 'tower' }): JSX.Element {
  const { t } = useFeedbaacks()
  return (
    <span class="fb-pill fb-agent-chip" data-testid={by === 'tower' ? 'feedbaacks-tower-chip' : 'feedbaacks-agent-approved-chip'}>
      {by === 'tower' ? <Send size={11} /> : <Bot size={11} />}
      {t(by === 'tower' ? 'handled_by_tower' : 'agent_will_handle')}
    </span>
  )
}
